import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useMatch } from "@/match/MatchContext";

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: {
      delay: 3.15 + i * 0.14,
      duration: 0.85,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

function MaskedLine({ index, children, className = "" }) {
  return (
    <span className="block overflow-hidden pb-[0.06em] -mb-[0.06em]">
      <motion.span
        className={`block will-change-transform ${className}`}
        variants={lineReveal}
        custom={index}
        initial="hidden"
        animate="show"
      >
        {children}
      </motion.span>
    </span>
  );
}

function PitchMarkings() {
  return (
    <svg
      className="absolute inset-0 w-full h-full"
      viewBox="0 0 800 1000"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      {/* halfway line */}
      <line x1="0" y1="500" x2="800" y2="500" stroke="#FFFFFF" strokeWidth="5" opacity="0.85" />
      {/* centre circle */}
      <circle cx="400" cy="500" r="230" fill="none" stroke="#FFFFFF" strokeWidth="5" opacity="0.85" />
      <circle cx="400" cy="500" r="8" fill="#FFFFFF" opacity="0.85" />
      {/* penalty arc suggestion bottom */}
      <path d="M 250 1000 A 190 190 0 0 1 550 1000" fill="none" stroke="#FFFFFF" strokeWidth="5" opacity="0.5" />
    </svg>
  );
}

export default function Hero({ photoUrl }) {
  const { kickOff, scrollTo } = useMatch();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={heroRef}
      data-testid="hero-section"
      id="kickoff"
      className="pitch-stripes relative min-h-screen pt-24 sm:pt-28 pb-14 px-4 sm:px-8 lg:px-16 flex items-center overflow-hidden border-b-4 border-black"
    >
      {/* scene label */}
      <span className="absolute top-24 sm:top-28 left-4 sm:left-8 lg:left-16 font-broadcast font-bold text-[9px] sm:text-[10px] tracking-[0.3em] uppercase text-black/50">
        Scene 01 · Where it all began
      </span>
      {/* corner flag detail */}
      <div className="absolute top-20 right-4 sm:right-8 w-3 h-24 sm:h-32 bg-[#FFDF00] border-2 border-black hidden md:block" aria-hidden="true" />

      <div className="max-w-[1600px] mx-auto w-full grid grid-cols-1 lg:grid-cols-[1.25fr_1fr] gap-12 lg:gap-8 items-center">
        {/* LEFT — title & controls */}
        <motion.div style={{ y: titleY }} className="relative z-10">
          {/* match-day label */}
          <motion.div
            data-testid="match-day-label"
            className="inline-flex items-center gap-3 bg-white border-2 border-black hard-shadow px-4 py-2 mb-7 sm:mb-9"
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 3.05, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="w-2 h-2 bg-[#FF3B30] live-dot" />
            <span className="font-broadcast font-bold text-[10px] sm:text-xs tracking-[0.28em] uppercase text-black">
              Match Day 01 · The Story So Far
            </span>
          </motion.div>

          {/* title */}
          <h1
            data-testid="hero-title"
            className="font-display uppercase leading-[0.86] tracking-tight text-black text-[19vw] sm:text-[15vw] lg:text-[9.2rem] xl:text-[10.5rem]"
          >
            <MaskedLine index={0}>Advaith</MaskedLine>
            <span className="block">
              <span className="inline-block overflow-hidden pb-[0.08em] -mb-[0.08em] align-middle mr-[0.12em]">
                <motion.span
                  data-testid="vs-badge"
                  className="vs-slam inline-block bg-[#0A0A0A] text-[#84E116] px-[0.18em] pt-[0.1em] pb-[0.06em] border-4 border-black will-change-transform"
                  style={{ animationDelay: "3.42s" }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 3.42, duration: 0.01 }}
                >
                  VS
                </motion.span>
              </span>
              <span className="inline-block overflow-hidden pb-[0.06em] -mb-[0.06em] align-middle">
                <motion.span
                  className="block will-change-transform"
                  variants={lineReveal}
                  custom={1}
                  initial="hidden"
                  animate="show"
                >
                  Life
                </motion.span>
              </span>
            </span>
          </h1>

          {/* tagline */}
          <motion.p
            data-testid="hero-tagline"
            className="mt-7 sm:mt-9 max-w-md bg-[#F2F2F0] border-2 border-black hard-shadow px-5 py-4 text-base sm:text-lg font-medium leading-snug text-black"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 3.6, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            90 minutes. A lifetime of decisions. One game still being played.
          </motion.p>

          {/* buttons */}
          <motion.div
            className="mt-7 sm:mt-9 flex flex-wrap items-center gap-4 sm:gap-5"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 3.75, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              type="button"
              onClick={() => {
                kickOff();
                scrollTo("#captain");
              }}
              data-testid="kick-off-btn"
              className="font-display uppercase text-lg sm:text-xl tracking-wide bg-[#0052FF] text-white border-[3px] border-black hard-shadow-lg px-8 sm:px-10 py-3.5 sm:py-4 transition-all duration-200 hover:bg-[#0041CC] hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[8px_8px_0_0_#000] active:translate-x-1 active:translate-y-1 active:shadow-none focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-black/40"
            >
              Kick Off
            </button>
            <button
              type="button"
              onClick={() => scrollTo("#final")}
              data-testid="skip-btn"
              className="font-display uppercase text-lg sm:text-xl tracking-wide bg-white text-black border-[3px] border-black hard-shadow-lg px-8 sm:px-10 py-3.5 sm:py-4 transition-all duration-200 hover:bg-[#F2F2F0] hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[8px_8px_0_0_#000] active:translate-x-1 active:translate-y-1 active:shadow-none focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-black/40"
            >
              Skip
            </button>
          </motion.div>
        </motion.div>

        {/* RIGHT — framed portrait on the pitch */}
        <motion.div
          data-testid="portrait-container"
          className="relative w-full max-w-[380px] sm:max-w-[440px] mx-auto lg:ml-auto"
          style={{ y: portraitY }}
          initial={{ opacity: 0, scale: 0.92, rotate: 3 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ delay: 3.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="relative border-4 border-black hard-shadow-xl overflow-hidden bg-[#84E116]">
            <PitchMarkings />
            {/* badge */}
            <div className="absolute top-3 left-3 z-20 bg-white border-2 border-black px-3 py-1.5">
              <span className="font-broadcast font-bold text-[10px] tracking-[0.25em] uppercase text-black">
                00:00 · Kick-Off
              </span>
            </div>
            {/* portrait slot 3:4 */}
            <div className="relative z-10 mx-6 sm:mx-8 mt-14 mb-16 aspect-[3/4] border-4 border-black bg-[#0A0A0A] overflow-hidden">
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt="Advaith"
                  className="w-full h-full object-cover"
                  data-testid="hero-portrait-image"
                />
              ) : (
                <div
                  className="w-full h-full flex flex-col items-center justify-center gap-4"
                  data-testid="hero-portrait-placeholder"
                >
                  <svg viewBox="0 0 64 64" className="w-16 h-16 sm:w-20 sm:h-20" aria-hidden="true">
                    <circle cx="32" cy="32" r="30" fill="none" stroke="#84E116" strokeWidth="3" />
                    <circle cx="32" cy="32" r="30" fill="none" stroke="#84E116" strokeWidth="1" opacity="0.35" transform="scale(0.72) translate(12.5 12.5)" />
                    <path d="M32 12 L42 20 L38 33 L26 33 L22 20 Z" fill="#84E116" />
                    <line x1="32" y1="12" x2="32" y2="2" stroke="#84E116" strokeWidth="2" />
                    <line x1="42" y1="20" x2="60" y2="16" stroke="#84E116" strokeWidth="2" />
                    <line x1="38" y1="33" x2="48" y2="48" stroke="#84E116" strokeWidth="2" />
                    <line x1="26" y1="33" x2="16" y2="48" stroke="#84E116" strokeWidth="2" />
                    <line x1="22" y1="20" x2="4" y2="16" stroke="#84E116" strokeWidth="2" />
                  </svg>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
